/**
 * Message Bubble Component
 * Following PRD Phase 3 specifications (lines 352-358)
 */

import React from 'react';
import { format } from 'date-fns';
import { Copy, Check, AlertCircle, Loader2, FileText, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Message } from '@/types/chat';
import { ExecutionResult } from './ExecutionResult';
import { VisualizationRenderer } from './visualizations';

interface MessageBubbleProps {
  message: Message;
  isLastMessage?: boolean;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isLastMessage }) => {
  const [copied, setCopied] = React.useState(false);
  const isUser = message.role === 'user';
  const isError = message.status === 'error';
  const isSending = message.status === 'sending';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  // Simple format - just preserve line breaks with proper alignment
  const formatContent = (content: string) => {
    return (
      <div className="whitespace-pre-wrap break-words">
        {content}
      </div>
    );
  };

  // Handle special message types
  if (message.messageType === 'execution-result' && message.executionResultData) {
    return (
      <div className="w-full mb-4">
        <ExecutionResult
          planId={message.executionResultData.planId || message.id}
          planDescription={message.executionResultData.planDescription || message.content}
          tasks={message.executionResultData.tasks || []}
          totalExecutionTime={message.executionResultData.totalExecutionTime || 0}
          completedAt={message.executionResultData.completedAt || message.timestamp.toISOString()}
          overallStatus={message.executionResultData.overallStatus || 'success'}
          actualResult={message.executionResultData.actualResult}
          statistics={message.executionResultData.statistics}
        />
      </div>
    );
  }

  // Skip rendering plan and execution monitor messages - they're handled differently
  if (message.messageType === 'plan' || message.messageType === 'execution') {
    return null; // Don't render these message types in the chat
  }

  // Special rendering for agent status messages - left aligned like agent messages
  if (message.messageType === 'agent-status') {
    return (
      <div className="flex w-full mb-2 justify-start">
        <div className="bg-muted/50 border border-border rounded px-3 py-1.5 max-w-[70%]">
          <span className="text-xs text-muted-foreground text-left block">
            {message.content}
          </span>
        </div>
      </div>
    );
  }

  // Visualization rendering - agent-generated charts, tables, etc.
  if (message.messageType === 'visualization' && message.visualizationData) {
    return (
      <div className="flex w-full mb-4 justify-start">
        <div className="max-w-[85%] w-full">
          {/* Optional text content before visualization */}
          {message.content && (
            <div className="bg-muted rounded-lg rounded-b-none px-4 py-3 text-sm">
              {message.content}
            </div>
          )}
          <VisualizationRenderer
            visualization={message.visualizationData}
            className={message.content ? "rounded-t-none" : ""}
          />
          {/* Timestamp */}
          <div className="flex items-center mt-2 gap-2 px-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground">
                    {format(message.timestamp, 'HH:mm')}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{format(message.timestamp, 'PPpp')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full mb-4',
        isUser ? 'justify-end' : 'justify-start',
        isLastMessage && 'mb-2'
      )}
    >
      <div
        className={cn(
          'relative max-w-[70%] rounded-lg px-4 py-3',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted',
          isError && 'bg-destructive/10 text-destructive border border-destructive/20',
          isSending && 'opacity-70'
        )}
      >
          {/* File attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mb-3 space-y-2">
              {message.attachments.map((attachment) => (
                <a
                  key={attachment.file_id}
                  href={attachment.public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "flex items-center gap-2 p-2 rounded border transition-colors",
                    isUser
                      ? "border-primary-foreground/20 hover:bg-primary-foreground/10"
                      : "border-border hover:bg-accent"
                  )}
                >
                  <FileText className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{attachment.filename}</p>
                    <p className={cn(
                      "text-xs",
                      isUser ? "opacity-70" : "text-muted-foreground"
                    )}>
                      {(attachment.file_size_bytes / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-50" />
                </a>
              ))}
            </div>
          )}

          {/* Message content - user text right-aligned, agent text left-aligned */}
          <div className={cn(
            "text-sm",
            isUser ? "text-right" : "text-left"
          )}>
            {formatContent(message.content)}
          </div>

        {/* Error indicator */}
        {isError && message.error && (
          <div className="flex items-center gap-1 mt-2 text-xs text-destructive">
            <AlertCircle className="h-3 w-3" />
            <span>{message.error}</span>
          </div>
        )}

        {/* Sending indicator */}
        {isSending && (
          <div className="absolute -bottom-1 right-2">
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Timestamp, step summary, and actions */}
        <div className="flex items-center justify-between mt-2 gap-2">
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs opacity-70">
                    {format(message.timestamp, 'HH:mm')}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{format(message.timestamp, 'PPpp')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Step summary for assistant messages */}
            {!isUser && message.stepSummary && (
              <span className="text-xs text-muted-foreground">
                • {message.stepSummary.steps} steps, {(message.stepSummary.duration / 1000).toFixed(1)}s
              </span>
            )}
          </div>

          {/* Copy button for assistant messages */}
          {!isUser && !isError && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-3 w-3" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};